import { useState, useMemo, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { FileUpload } from '@/components/ui/file-upload';
import {
  Upload, Loader2, Check, AlertTriangle, FileSpreadsheet,
  ClipboardCheck, X, Clock,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { buildTimestamp, matchDriverByTag, statementDateToYmd } from '@/lib/gt-repayments';
import { formatBusinessDate } from '@/lib/format';
import { driverProfileName } from '@/lib/driverProfile';
import { applyRefIssues } from './importReferences';
import type { ExistingTxn, ReferenceRow } from './importReferences';
import { bookingAttention, describeBookingPlan, describeSkipped, importHeadline, planBookings } from './importReversals';
import type { InsertedRow, SkippedBooking } from './importReversals';
import { importStatement, mergeAwaiting, retryBookings, stillAwaiting } from './importBooking';
import { fetchTransactionEmails, type TransactionEmail } from './importEmails';
import type { AwaitingBooking, BookingDeps, InsertCounts, RetryDeps, Settled } from './importBooking';
import type { BankStatementRow } from '@/lib/sbt-pdf';

// ─── Types ───────────────────────────────────────────────────────────────────

interface ImportTransactionsSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * A parsed transaction ready for review/import. `ReferenceRow` carries the identity and the
 * reference state — which value the row will write, which one the bank gave it, and whether that
 * value can be written at all — because those are decided for the whole list together.
 */
interface ParsedTransaction extends ReferenceRow {
  /** Transaction date from XLS (value date) */
  transDate: string;
  /** Transaction time from GT email match, or null */
  transactionTime: string | null;
  /** Main amount (debit or credit value, always positive) */
  amount: number;
  /** Fee amount (from a sibling row with same reference), 0 if none */
  fee: number;
  /** Description / remarks from bank statement */
  description: string;
  /** CREDIT or DEBIT */
  type: 'CREDIT' | 'DEBIT';
  /** GHS or USD */
  currency: 'GHS' | 'USD';
  /** Matched driver display name (for CREDIT repayments) */
  matchedDriverName: string | null;
  /** Matched contract ID (for repayment linking) */
  matchedContractId: number | null;
}

// ─── Steps ───────────────────────────────────────────────────────────────────

const STEPS = [
  { key: 'upload', label: 'Upload Files', icon: Upload, description: 'Bank statement' },
  { key: 'review', label: 'Review', icon: ClipboardCheck, description: 'Verify parsed transactions' },
  { key: 'confirm', label: 'Confirm', icon: Check, description: 'Import into database' },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatCurrency(n: number, currency: string) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(n);
}

/**
 * The day the review row shows must be the day `buildTimestamp` will actually STORE, so both read the
 * statement string through `parseStatementDate`. `new Date(dateStr)` disagreed with it twice over: it
 * resolved an ISO date-only row to UTC midnight and then rendered it in the viewer's zone (a day early
 * at negative offsets), and it silently guessed at the all-numeric shapes ("04/19/2026") that
 * `buildTimestamp` refuses \u2014 so the table could show a date that was never imported.
 */
function formatDate(dateStr: string): string {
  const ymd = statementDateToYmd(dateStr);
  if (!ymd) return dateStr;
  return new Date(`${ymd}T00:00:00Z`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

/**
 * Detect currency from XLS content by looking at the account number.
 * GTBank USD accounts: 32250020xxxxx, GHS accounts: 32250010xxxxx
 *
 * We extract from the `lblAcctNo` span in the header to avoid false
 * positives from account numbers embedded in transaction descriptions.
 */
function detectCurrency(htmlContent: string): 'GHS' | 'USD' {
  // Extract the official account number from the GT Bank header
  const acctMatch = htmlContent.match(/id="lblAcctNo"[^>]*>(\d+)</);
  if (acctMatch) {
    const acctNo = acctMatch[1];
    if (acctNo.startsWith('3225002')) return 'USD';
    if (acctNo.startsWith('3225001')) return 'GHS';
  }

  // Fallback: broad content search (kept for non-standard exports)
  if (/SBT\s+CO/i.test(htmlContent)) return 'USD';
  return 'GHS';
}

/** Extract doc number from a remarks string for GT email matching */
function extractDocNumber(remarks: string): string | null {
  const sbtPattern = remarks.match(/\bSG\s+\d{5,7}\s+(\d{12,13})/i);
  if (sbtPattern) return sbtPattern[1];
  const standalone = remarks.match(/\b(\d{10,20})\b/);
  return standalone ? standalone[1] : null;
}

/** One statement row into `transactions`: its id, or the database's refusal */
async function insertTransaction(t: ParsedTransaction) {
  const { data, error } = await supabase
    .from('transactions')
    .insert({
      amount: t.amount,
      fee: t.fee > 0 ? t.fee : null,
      reference: t.reference,
      description: t.description,
      transaction_at: buildTimestamp(t.transDate, t.transactionTime),
      type: t.type,
      currency: t.currency,
    })
    .select('id')
    .single();
  return { id: data?.id ?? null, error };
}

const bookingDeps: BookingDeps = {
  rpc: ids => supabase.rpc('get_transaction_reversals', { p_batch_ids: ids }),
  insertRepayment: booking => supabase.from('repayments').insert(booking),
  sleep: ms => new Promise(resolve => setTimeout(resolve, ms)),
};

const retryDeps: RetryDeps = {
  ...bookingDeps,
  purposes: ids => supabase.rpc('get_transaction_purposes', { transaction_ids: ids }),
};

// ─── Component ───────────────────────────────────────────────────────────────

export default function ImportTransactionsSheet({ open, onOpenChange }: ImportTransactionsSheetProps) {
  const queryClient = useQueryClient();
  const [step, setStep] = useState(0);

  // Step 1: Files
  const [bankFile, setBankFile] = useState<File | null>(null);


  // Processing
  const [processing, setProcessing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState('');

  // Step 2: Review
  const [transactions, setTransactions] = useState<ParsedTransaction[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [existingRefs, setExistingRefs] = useState<ReadonlyMap<string, ExistingTxn>>(new Map());
  const [detectedCurrency, setDetectedCurrency] = useState<'GHS' | 'USD'>('GHS');

  // Step 3: Confirm
  const [confirming, setConfirming] = useState(false);
  const [importDone, setImportDone] = useState(false);
  const [importSummary, setImportSummary] = useState('');
  const [importNotes, setImportNotes] = useState<string[]>([]);
  const [awaiting, setAwaiting] = useState<AwaitingBooking[]>([]);
  const [retryingBooking, setRetryingBooking] = useState(false);
  // Which opening of the sheet an import belongs to. The check can keep an import running for
  // seconds, and one that settles after the sheet was closed must not leave its done step behind
  // for the next opening to find.
  const runRef = useRef(0);
  // Every import of this opening still waiting for Retry, oldest first. A run settles seconds after
  // it began, and another may have settled in between, so it reads this — never its own render's state.
  const awaitingRef = useRef<AwaitingBooking[]>([]);
  const holdAwaiting = (next: AwaitingBooking[]) => {
    awaitingRef.current = next;
    setAwaiting(next);
  };

  const handleClose = () => {
    runRef.current++;
    onOpenChange(false);
    setTimeout(() => {
      setStep(0);
      setBankFile(null);

      setTransactions([]);
      setSelected(new Set());
      setExistingRefs(new Map());
      setProcessing(false);
      setConfirming(false);
      setImportDone(false);
      setImportSummary('');
      setImportNotes([]);
      holdAwaiting([]);
      setRetryingBooking(false);
    }, 300);
  };

  const isStepComplete = (i: number) => {
    switch (i) {
      case 0: return transactions.length > 0;
      case 1: return selected.size > 0;
      case 2: return importDone;
      default: return false;
    }
  };

  // ─── Process files ────────────────────────────────────────────────────────

  const handleProcess = async () => {
    if (!bankFile) return;
    setProcessing(true);

    try {
      setProcessingStatus('Parsing bank statement...');
      const { parseGhsBankStatementXls } = await import('@/lib/sbt-pdf');

      const xlsText = await bankFile.text();
      const currency = detectCurrency(xlsText);
      setDetectedCurrency(currency);

      const allRows: BankStatementRow[] = parseGhsBankStatementXls(xlsText);

      if (allRows.length === 0) {
        toast.error('No transactions found', { description: 'The bank statement appears empty or unrecognized.' });
        setProcessing(false);
        return;
      }

      // Reject statements with dates before reference-based imports began
      const REF_IMPORT_CUTOFF = new Date('2026-04-24');
      const parsedDates = allRows
        .map(r => new Date(buildTimestamp(r.valueDate, null)))
        .filter(d => !isNaN(d.getTime()));

      const earliestDate = parsedDates.length > 0
        ? new Date(Math.min(...parsedDates.map(d => d.getTime())))
        : null;

      if (earliestDate && earliestDate < REF_IMPORT_CUTOFF) {
        toast.error('Statement too old', {
          // `buildTimestamp` returns the Ghana calendar day as a UTC instant, so it has to be read
          // back in UTC: formatted in the operator's own zone, a Ghana midnight reads as the day
          // before from anywhere west of Accra.
          description: `Earliest date is ${earliestDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })}. Only statements from Apr 24, 2026 onwards are supported.`,
        });
        setProcessing(false);
        return;
      }

      // Extract doc numbers for GT email matching
      for (const row of allRows) {
        row.docNumber = extractDocNumber(row.remarks);
      }

      // Fetch archived email notifications from transaction_emails table
      setProcessingStatus('Loading email notifications...');
      const dates = allRows
        .map(r => statementDateToYmd(r.valueDate))
        .filter(Boolean) as string[];

      // Every email in the span, by id; it throws on a failed or short read (see importEmails.ts),
      // which lands in the catch below before anything is staged.
      let emailNotifications: TransactionEmail[] = [];
      if (dates.length > 0) {
        const minDate = dates.reduce((a, b) => a < b ? a : b);
        const maxDate = dates.reduce((a, b) => a > b ? a : b);
        emailNotifications = await fetchTransactionEmails(minDate, maxDate);
      }

      // Build doc number → email notification lookup
      const gtByDoc = new Map<string, TransactionEmail>();
      for (const n of emailNotifications) {
        if (n.doc_number) gtByDoc.set(n.doc_number, n);
      }

      // Track which notifications have been consumed
      const usedIndexes = new Set<number>();

      /**
       * Match a bank row to an archived email notification.
       * 
       * Strategy (most to least specific):
       * 1. Description prefix: email description is a truncated prefix of bank remarks
       *    (GT emails truncate at ~80 chars). Filter by amount + type first.
       * 2. Amount + date + type: fallback for transactions with no/mismatched descriptions.
       *    Only used when there's exactly one unconsumed match to avoid ambiguity.
       */
      const findGtMatch = (
        bankRemarks: string,
        amount: number,
        valueDate: string,
        txType: 'CREDIT' | 'DEBIT',
      ): TransactionEmail | null => {
        const bankDateStr = statementDateToYmd(valueDate);
        if (!bankDateStr) return null;
        const bankRemarksLower = bankRemarks.toLowerCase();

        // 1. Description prefix match (filtered by amount + date + type)
        // Collapse whitespace for comparison — bank XLS inserts extra spaces
        const bankNorm = bankRemarksLower.replace(/\s+/g, ' ');
        for (let i = 0; i < emailNotifications.length; i++) {
          if (usedIndexes.has(i)) continue;
          const n = emailNotifications[i];
          if (n.type !== txType) continue;
          if (Math.abs(n.amount - amount) > 0.01) continue;
          if (n.value_date !== bankDateStr) continue;

          // Email description is a truncated prefix of the bank remarks
          if (n.description && n.description.length >= 10) {
            const emailNorm = n.description.toLowerCase().replace(/\s+/g, ' ');
            if (bankNorm.startsWith(emailNorm) || bankNorm.includes(emailNorm)) {
              usedIndexes.add(i);
              return n;
            }
          }
        }

        // 2. Amount + date + type fallback — only if exactly one match remains
        const candidates: number[] = [];
        for (let i = 0; i < emailNotifications.length; i++) {
          if (usedIndexes.has(i)) continue;
          const n = emailNotifications[i];
          if (n.type !== txType) continue;
          if (Math.abs(n.amount - amount) > 0.01) continue;
          if (n.value_date !== bankDateStr) continue;
          candidates.push(i);
        }
        if (candidates.length === 1) {
          usedIndexes.add(candidates[0]);
          return emailNotifications[candidates[0]];
        }

        return null;
      };

      // Group rows by reference to pair main transactions with fees
      const byRef = new Map<string, BankStatementRow[]>();
      for (const row of allRows) {
        if (!byRef.has(row.reference)) byRef.set(row.reference, []);
        byRef.get(row.reference)!.push(row);
      }

      // Build final transaction list
      const parsed: Omit<ParsedTransaction, 'rowId' | 'refIssue' | 'refEditable'>[] = [];
      const processedRefs = new Set<string>();

      for (const [reference, refRows] of byRef) {
        if (processedRefs.has(reference)) continue;
        processedRefs.add(reference);

        const debitRows = refRows.filter(r => r.debit > 0);
        const creditRows = refRows.filter(r => r.credit > 0);

        // Handle debit transactions (main + fee grouping)
        if (debitRows.length > 0) {
          debitRows.sort((a, b) => b.debit - a.debit);
          const main = debitRows[0];
          const feeRow = debitRows.length > 1 && debitRows[debitRows.length - 1].debit < main.debit
            ? debitRows[debitRows.length - 1]
            : null;

          const gtMatch = findGtMatch(main.remarks, main.debit, main.valueDate, 'DEBIT');

          parsed.push({
            reference,
            originalReference: reference,
            transDate: main.valueDate,
            transactionTime: gtMatch?.time ?? null,
            amount: main.debit,
            fee: feeRow?.debit ?? 0,
            description: main.remarks,
            type: 'DEBIT',
            currency,
            matchedDriverName: null,
            matchedContractId: null,
          });

          /*
           * GT pairs a transfer with its fee under one reference, and that pair is all this grouping
           * can account for. A FURTHER debit row under the same reference is a transaction in its own
           * right — the bank reusing the reference — yet taking only the largest row and the smallest
           * dropped it: with three debits the middle one never reached the table, and two debits of
           * EQUAL value lost the second (no row is smaller than the main, so neither is a fee). The
           * money went missing from the ledger with nothing on screen to say so. Each extra is
           * surfaced instead, flagged on the reference it shares so its own can be given to it.
           */
          for (const extra of debitRows) {
            if (extra === main || extra === feeRow) continue;
            const extraMatch = findGtMatch(extra.remarks, extra.debit, extra.valueDate, 'DEBIT');

            parsed.push({
              reference,
              originalReference: reference,
              transDate: extra.valueDate,
              transactionTime: extraMatch?.time ?? null,
              amount: extra.debit,
              fee: 0,
              description: extra.remarks,
              type: 'DEBIT',
              currency,
              matchedDriverName: null,
              matchedContractId: null,
            });
          }
        }

        // Handle credit transactions
        if (creditRows.length > 0) {
          for (const cr of creditRows) {
            // Suffixing a reference the statement never gave invents one ("-CR0") that is neither the
            // bank's nor correctable — the rows stay blank instead, which is a state the admin can fix
            const crRef = creditRows.length > 1 && reference !== ''
              ? `${reference}-CR${creditRows.indexOf(cr)}`
              : reference;
            const gtMatch = findGtMatch(cr.remarks, cr.credit, cr.valueDate, 'CREDIT');

            parsed.push({
              reference: crRef,
              originalReference: crRef,
              transDate: cr.valueDate,
              transactionTime: gtMatch?.time ?? null,
              amount: cr.credit,
              fee: 0,
              description: cr.remarks,
              type: 'CREDIT',
              currency,
              matchedDriverName: null,
              matchedContractId: null,
            });
          }
        }
      }

      // ── Post-group: merge GHS interbank send fees ──
      // GHS "GIP - SEND FEE" rows get a different reference from their
      // parent "INSTANT PAY TRF" row. Match them by timestamp prefix.
      const tsPrefix = (desc: string) =>
        desc.match(/^(\d{12,14})_\*\*\/_/)?.[1] ?? null;

      for (let i = parsed.length - 1; i >= 0; i--) {
        const fee = parsed[i];
        if (fee.type !== 'DEBIT' || !fee.description.includes('SEND FEE')) continue;
        const feeTs = tsPrefix(fee.description);
        if (!feeTs) continue;

        const mainIdx = parsed.findIndex(
          (m) =>
            m.type === 'DEBIT' &&
            m.description.includes('INSTANT PAY TRF') &&
            tsPrefix(m.description) === feeTs &&
            m.transDate === fee.transDate,
        );
        if (mainIdx !== -1) {
          parsed[mainIdx].fee += fee.amount;
          parsed.splice(i, 1);
        }
      }

      // Sort by date + time descending
      parsed.sort((a, b) => {
        const tsA = new Date(buildTimestamp(a.transDate, a.transactionTime)).getTime();
        const tsB = new Date(buildTimestamp(b.transDate, b.transactionTime)).getTime();
        return tsB - tsA;
      });

      // Identity is handed out only once the list can no longer be spliced or reordered
      const built: ParsedTransaction[] = parsed.map((t, i) => ({
        ...t, rowId: `row-${i}`, refIssue: null, refEditable: false,
      }));

      /*
       * The duplicate probe asks about the references THESE ROWS WILL INSERT, not the statement's raw
       * reference column. A reference carrying several credits is written suffixed (`…-CR1`), so
       * asking about the unsuffixed value answered a question no row was going to ask: a re-import of
       * that statement came back "New", was ticked by default, and was refused by the unique
       * constraint on a row the admin had no way to correct.
       */
      setProcessingStatus('Checking for duplicates...');
      const probeRefs = [...new Set(built.map(t => t.reference).filter(Boolean))];
      const dbRefs = new Map<string, ExistingTxn>();
      for (let i = 0; i < probeRefs.length; i += 200) {
        const batch = probeRefs.slice(i, i + 200);
        const { data: existing } = await supabase
          .from('transactions')
          .select('reference, amount, currency, transaction_at')
          .in('reference', batch);
        for (const row of existing || []) {
          if (row.reference) {
            dbRefs.set(row.reference, {
              amount: row.amount,
              currency: row.currency,
              transactionAt: row.transaction_at,
            });
          }
        }
      }
      setExistingRefs(dbRefs);

      const rows = applyRefIssues(built, dbRefs);

      // Match CREDIT transactions to drivers for repayment linking
      if (currency === 'GHS') {
        setProcessingStatus('Matching drivers...');

        const { data: driverRows } = await supabase
          .from('drivers')
          .select('id, repayment_tag, contracts(id, created_at)');

        // For each driver, find their active (IN_PROGRESS) contract
        const driverContractIds = new Map<number, number>();
        const allContractIds = (driverRows || []).flatMap((d: any) =>
          (d.contracts || []).map((c: any) => c.id)
        );

        if (allContractIds.length > 0) {
          // Batch-fetch the latest status for all contracts at once
          const { data: statusRows } = await supabase
            .rpc('get_active_contract_per_driver', { p_driver_ids: (driverRows || []).map((d: any) => d.id) });

          for (const row of statusRows || []) {
            driverContractIds.set(row.driver_id, row.contract_id);
          }
        }

        // Fallback: for drivers without an active contract, use the newest
        for (const d of driverRows || []) {
          if (driverContractIds.has(d.id)) continue;
          const contracts = (d as any).contracts || [];
          if (contracts.length === 0) continue;
          const sorted = [...contracts].sort((a: any, b: any) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
          );
          driverContractIds.set(d.id, sorted[0].id);
        }

        const driverIds = (driverRows || []).map((d: any) => d.id);
        /*
         * `.in('driver_id', …)` RETURNS EVERY ROW AGAINST A DRIVER ID, NOT ONE PER DRIVER.
         * `profiles.driver_id` is unique only WHERE `admin_id IS NULL`, so an admin testing as a
         * driver holds a second row against that same id — and a map written row by row keeps
         * whichever arrived LAST. This map names the driver a bank credit is about to be assigned
         * to, so the admin's name landing on it is a wrong label over real money.
         *
         * `admin_id` IS THEREFORE SELECTED AND THE PICK IS `driverProfileName`'s: the rows are
         * gathered per driver first, then the driver's own one is chosen. A driver whose only row
         * belongs to an admin gets NO name, and falls through to their repayment tag below —
         * never to the admin's name.
         */
        const { data: profileRows } = await supabase
          .from('profiles')
          .select('id, driver_id, admin_id, first_name, last_name')
          .in('driver_id', driverIds);

        const rowsByDriver = new Map<number, NonNullable<typeof profileRows>>();
        for (const p of profileRows || []) {
          if (!p.driver_id) continue;
          const rows = rowsByDriver.get(p.driver_id);
          if (rows) rows.push(p);
          else rowsByDriver.set(p.driver_id, [p]);
        }

        const profileMap = new Map<number, string>();
        for (const [driverId, rows] of rowsByDriver) {
          const name = driverProfileName(rows);
          if (name) profileMap.set(driverId, name);
        }

        const driverOptions = (driverRows || []).map((d: any) => ({
          id: d.id,
          repaymentTag: d.repayment_tag,
          name: profileMap.get(d.id) || d.repayment_tag || `Driver #${d.id}`,
          contractId: driverContractIds.get(d.id) ?? null,
        }));

        for (const tx of rows) {
          if (tx.type !== 'CREDIT') continue;
          const match = matchDriverByTag(tx.description, driverOptions);
          if (match) {
            tx.matchedDriverName = match.driverName;
            tx.matchedContractId = match.contractId;
          }
        }
      }

      setTransactions(rows);

      const autoSelected = new Set(
        rows.filter(t => t.refIssue === null).map(t => t.rowId)
      );
      setSelected(autoSelected);
      setStep(1);
    } catch (err) {
      toast.error('Failed to process files', { description: (err as Error).message });
    } finally {
      setProcessing(false);
      setProcessingStatus('');
    }
  };

  // ─── Confirm Import ────────────────────────────────────────────────────────

  /*
   * What the done step and the toast say once a batch is settled. The rows are in whatever became
   * of the sheet, so the refetches and the toast always go out; the done step and the Retry set are
   * written only when the run that settled is still this opening's (`live`). `retried` is the
   * imports a Retry took, null for an import.
   */
  const reportSettled = (
    live: boolean, rows: InsertedRow[], counts: InsertCounts, settled: Settled,
    retried: readonly AwaitingBooking[] | null, skipped: SkippedBooking[] = [],
  ) => {
    for (const key of [
      'transactions', 'transactions-count', 'repayments', 'contract-repayments',
      'all-contract-transactions', 'transaction-stats', 'unassigned-transactions',
      'unassigned-transactions-count', 'unassigned-debit-transactions', 'transaction-purposes',
    ]) {
      queryClient.invalidateQueries({ queryKey: [key] });
    }

    const { plan } = settled;
    // This run's rows wait for Retry when its check did not answer, alongside every earlier import
    // still waiting — those keep their rows and their note until a Retry resolves them. A closed
    // sheet has no Retry, so a run that settles after it closed only says what it did.
    const own = live && plan.pending.length > 0 ? { rows, counts } : null;
    const waiting = live ? stillAwaiting(awaitingRef.current, retried ?? [], own) : [];
    const waitingRows = mergeAwaiting(waiting).rows;
    const waitingPlan = waiting.length > 0 ? planBookings(waitingRows, null) : null;
    const summary = importHeadline({ ...counts, booked: settled.booked, bookingFailed: settled.bookingFailed });
    const notes = [
      ...describeSkipped(skipped),
      // This run's own "not booked yet" is said once, with the rest of what is waiting
      ...(own ? [] : describeBookingPlan(plan, rows, live)),
      ...(waitingPlan ? describeBookingPlan(waitingPlan, waitingRows) : []),
    ];
    if (live) {
      setImportSummary(summary);
      setImportNotes(notes);
      holdAwaiting(waiting);
    }

    const description = [summary, ...notes].join(' ');
    const attention = settled.bookingFailed > 0 || skipped.length > 0
      ? 'Import complete — some repayments not booked'
      : bookingAttention(plan) ?? (waitingPlan && bookingAttention(waitingPlan));
    if (!retried && counts.rejected + counts.failed > 0) toast.warning('Import finished with failures', { description });
    else if (attention) toast.warning(attention, { description });
    else toast.success(retried ? 'Repayments booked' : 'Import complete', { description });
  };

  const handleConfirm = async () => {
    const run = runRef.current;
    setConfirming(true);

    try {
      const toImport = transactions.filter(t => selected.has(t.rowId) && t.refIssue === null);
      const { inserted, counts, ...settled } = await importStatement(toImport, { ...bookingDeps, insertTransaction });
      const live = run === runRef.current;
      reportSettled(live, inserted, counts, settled, null);
      if (live) {
        setImportDone(true);
        setStep(2);
      }
    } catch (err) {
      toast.error('Import failed', { description: (err as Error).message });
    } finally {
      if (run === runRef.current) setConfirming(false);
    }
  };

  const handleRetryBooking = async () => {
    const retried = awaitingRef.current;
    if (retried.length === 0) return;
    const { rows, counts } = mergeAwaiting(retried);
    const run = runRef.current;
    setRetryingBooking(true);
    try {
      const { rows: retriedRows, skipped, ...settled } = await retryBookings(rows, retryDeps);
      reportSettled(run === runRef.current, retriedRows, counts, settled, retried, skipped);
    } catch (err) {
      toast.error('Booking failed', { description: (err as Error).message });
    } finally {
      if (run === runRef.current) setRetryingBooking(false);
    }
  };

  // ─── Toggle helpers ────────────────────────────────────────────────────────

  const selectableTransactions = useMemo(
    () => transactions.filter(t => t.refIssue === null),
    [transactions]
  );

  const toggleRow = (rowId: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId); else next.add(rowId);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected(selected.size === selectableTransactions.length
      ? new Set()
      : new Set(selectableTransactions.map(t => t.rowId))
    );
  };

  // ─── Reference edits ───────────────────────────────────────────────────────

  /*
   * The parse-time probe only asked about the references in this statement. An edit can land on one
   * an EARLIER import already stored — invisible to that batch, and rejected by the unique constraint
   * at insert time. What comes back is a fact about the VALUE, not about the row that asked, so it is
   * recorded against the value and every row is re-judged against it.
   */
  const verifyReferenceAgainstDb = async (ref: string) => {
    const { data } = await supabase
      .from('transactions')
      .select('reference, amount, currency, transaction_at')
      .eq('reference', ref)
      .limit(1);
    const hit = data?.[0];
    if (!hit) return;

    setExistingRefs(prev => prev.has(ref) ? prev : new Map(prev).set(ref, {
      amount: hit.amount,
      currency: hit.currency,
      transactionAt: hit.transaction_at,
    }));
  };

  /*
   * GT sometimes reuses a reference for a genuinely different transaction, so a flagged row is not
   * always a re-import — the admin can make the reference unique and let the row through. Editing is
   * confined to rows whose reference could not be written at some point: on a row that has always
   * been importable the reference is the dedup key a future statement will be matched against, and a
   * changed one imports twice in silence.
   */
  const commitReference = (rowId: string, raw: string) => {
    const value = raw.trim();

    // Judging the whole list, not just this row, is what lets a row blocked by THIS one's old value
    // come back: the block is a fact about who holds the value, and this edit may have released it.
    setTransactions(prev => applyRefIssues(
      prev.map(t => (t.rowId === rowId ? { ...t, reference: value } : t)),
      existingRefs,
    ));

    // Clearing the flag is not the admin's tick — the row is only enabled, never selected, here.
    if (value !== '' && !existingRefs.has(value)) void verifyReferenceAgainstDb(value);
  };

  /** A value the DB turned out to hold blocks whichever row is on it, whenever the answer lands. */
  useEffect(() => {
    setTransactions(prev => applyRefIssues(prev, existingRefs));
  }, [existingRefs]);

  /** A row that has gone back to colliding must not carry a tick into the insert. */
  useEffect(() => {
    const blocked = new Set(transactions.filter(t => t.refIssue !== null).map(t => t.rowId));
    setSelected(prev => {
      if (![...prev].some(id => blocked.has(id))) return prev;
      const next = new Set(prev);
      for (const id of blocked) next.delete(id);
      return next;
    });
  }, [transactions]);

  // ─── Summary stats ────────────────────────────────────────────────────────

  const summaryStats = useMemo(() => {
    const selectedTxns = transactions.filter(t => selected.has(t.rowId) && t.refIssue === null);
    const credits = selectedTxns.filter(t => t.type === 'CREDIT');
    const debits = selectedTxns.filter(t => t.type === 'DEBIT');
    const totalCredits = credits.reduce((s, t) => s + t.amount, 0);
    const totalDebits = debits.reduce((s, t) => s + t.amount + t.fee, 0);
    const totalFees = selectedTxns.reduce((s, t) => s + t.fee, 0);
    const duplicateCount = transactions.filter(t => t.refIssue === 'db').length;
    // A row held back by another row of this same statement is NOT already imported, and saying so
    // would send the admin looking for a ledger entry that does not exist.
    const conflictCount = transactions.filter(
      t => t.refIssue === 'batch' || t.refIssue === 'blank'
    ).length;
    const driverMatchCount = selectedTxns.filter(t => t.matchedContractId !== null).length;

    return {
      creditCount: credits.length,
      debitCount: debits.length,
      totalCredits,
      totalDebits,
      totalFees,
      duplicateCount,
      conflictCount,
      selectedCount: selectedTxns.length,
      driverMatchCount,
    };
  }, [transactions, selected]);

  // ─── Render row ────────────────────────────────────────────────────────────

  const renderRow = (t: ParsedTransaction) => {
    const isBlocked = t.refIssue !== null;
    const isSelected = selected.has(t.rowId);
    const isDebit = t.type === 'DEBIT';
    // What the reference is ALREADY spent on. Told only "duplicate", the admin cannot tell a genuine
    // re-import from GT reusing the reference — and retyping a true re-import books the money twice.
    const stored = t.refIssue === 'db' ? existingRefs.get(t.reference) : undefined;

    return (
      <tr
        key={t.rowId}
        className={cn(
          'border-b last:border-0 transition-colors',
          isBlocked ? 'opacity-40 bg-muted/10' :
            isSelected ? 'bg-white' : 'bg-muted/20 opacity-60',
        )}
      >
        <td className="px-3 py-2">
          <Checkbox
            checked={isSelected}
            onCheckedChange={() => toggleRow(t.rowId)}
            disabled={isBlocked}
          />
        </td>
        <td className="px-3 py-2 whitespace-nowrap">
          <div className="flex flex-col gap-0.5">
            <span className="text-xs">{formatDate(t.transDate)}</span>
            {!isBlocked ? (
              <div className="flex items-center">
                <input
                  type="time"
                  step="1"
                  defaultValue={(() => {
                    if (!t.transactionTime) return '';
                    const m = t.transactionTime.match(/(\d{1,2}):(\d{2}):?(\d{2})?\s*(AM|PM)/i);
                    if (!m) return '';
                    let h = parseInt(m[1]);
                    const period = m[4].toUpperCase();
                    if (period === 'PM' && h !== 12) h += 12;
                    if (period === 'AM' && h === 12) h = 0;
                    return `${String(h).padStart(2, '0')}:${m[2]}:${m[3] || '00'}`;
                  })()}
                  className="text-[10px] text-muted-foreground border border-dashed border-muted-foreground/30 rounded px-1 py-0 w-[85px] bg-transparent focus:outline-none focus:border-primary"
                  onChange={(e) => {
                    if (!e.target.value) return;
                    const [h, m, s] = e.target.value.split(':').map(Number);
                    const hr12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
                    const period = h >= 12 ? 'PM' : 'AM';
                    const timeStr = `${hr12}:${String(m).padStart(2, '0')}:${String(s || 0).padStart(2, '0')} ${period}`;
                    setTransactions(prev => prev?.map(tx =>
                      tx.rowId === t.rowId ? { ...tx, transactionTime: timeStr } : tx
                    ) ?? null);
                  }}
                />
              </div>
            ) : t.transactionTime ? (
              <span className="text-[10px] text-muted-foreground">{t.transactionTime}</span>
            ) : null}
          </div>
        </td>
        <td className="px-3 py-2 max-w-[160px]">
          {t.refEditable ? (
            <input
              defaultValue={t.originalReference}
              spellCheck={false}
              className="w-full text-[11px] font-mono text-muted-foreground border border-dashed border-muted-foreground/30 rounded px-1 py-0 bg-transparent focus:outline-none focus:border-primary"
              onChange={(e) => commitReference(t.rowId, e.target.value)}
            />
          ) : (
            <p className="text-[11px] font-mono text-muted-foreground break-all leading-snug">
              {t.reference || '—'}
            </p>
          )}
        </td>
        <td className="px-3 py-2 max-w-[250px]">
          <p className="text-xs break-all whitespace-normal leading-snug">
            {t.description || '—'}
          </p>
        </td>
        <td className={cn('px-3 py-2 text-right whitespace-nowrap font-medium text-xs', isDebit ? 'text-red-600' : 'text-emerald-600')}>
          {isDebit ? '-' : '+'}{formatCurrency(t.amount, t.currency)}
        </td>
        <td className="px-3 py-2 text-right whitespace-nowrap text-xs text-orange-600">
          {t.fee > 0 ? formatCurrency(t.fee, t.currency) : '—'}
        </td>
        <td className="px-3 py-2 whitespace-nowrap">
          {t.matchedDriverName ? (
            <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-700 border-blue-200">
              {t.matchedDriverName}
            </Badge>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          )}
        </td>
        <td className="px-3 py-2 whitespace-nowrap">
          {t.refIssue === 'db' ? (
            <div className="flex flex-col gap-0.5">
              <Badge variant="outline" className="text-[10px] bg-yellow-50 text-yellow-700 border-yellow-200 w-fit">Imported</Badge>
              {stored && (
                <span className="text-[10px] text-muted-foreground">
                  {formatCurrency(stored.amount, stored.currency)} · {formatBusinessDate(stored.transactionAt)}
                </span>
              )}
            </div>
          ) : t.refIssue === 'batch' ? (
            <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">Duplicate ref</Badge>
          ) : t.refIssue === 'blank' ? (
            <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">No reference</Badge>
          ) : (
            <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">New</Badge>
          )}
        </td>
      </tr>
    );
  };

  // ─── Step Content ──────────────────────────────────────────────────────────

  const renderStepContent = () => {
    switch (step) {
      case 0: // Upload
        return (
          <div className="space-y-5">
            <div className="space-y-1.5">
              <label className="text-sm font-medium flex items-center gap-2">
                Bank Statement
                <Badge variant="secondary" className="text-[10px] px-1.5 h-4">Required</Badge>
              </label>
              <FileUpload file={bankFile} setFile={setBankFile} accept=".xls" maxSizeMB={10} />
              <p className="text-xs text-muted-foreground">GTBank statement XLS file. Supports both GHS and USD accounts.</p>
              <p className="text-xs text-muted-foreground">Transaction times are automatically enriched from archived email notifications.</p>
            </div>
          </div>
        );

      case 1: // Review
        return (
          <div className="space-y-4">
            {/* Summary badges */}
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                {summaryStats.creditCount} credit{summaryStats.creditCount !== 1 ? 's' : ''}
              </Badge>
              {summaryStats.debitCount > 0 && (
                <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                  {summaryStats.debitCount} debit{summaryStats.debitCount !== 1 ? 's' : ''}
                </Badge>
              )}
              {summaryStats.driverMatchCount > 0 && (
                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                  {summaryStats.driverMatchCount} driver repayment{summaryStats.driverMatchCount !== 1 ? 's' : ''}
                </Badge>
              )}
              {summaryStats.duplicateCount > 0 && (
                <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                  {summaryStats.duplicateCount} already imported
                </Badge>
              )}
              {summaryStats.conflictCount > 0 && (
                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                  {summaryStats.conflictCount} reference conflict{summaryStats.conflictCount !== 1 ? 's' : ''}
                </Badge>
              )}
              <Badge variant="outline">{detectedCurrency}</Badge>
            </div>

            {/* Table */}
            <div className="border rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[900px]">
                  <thead>
                    <tr className="border-b bg-muted/30">
                      <th className="px-3 py-2 text-left w-8">
                        <Checkbox
                          checked={selected.size === selectableTransactions.length && selectableTransactions.length > 0}
                          onCheckedChange={toggleAll}
                        />
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Date</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Reference</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Description</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground">Amount</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground">Fee</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Driver</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map(t => renderRow(t))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );

      case 2: // Done
        return (
          <div className="flex flex-col items-center justify-center py-16 space-y-4">
            <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center">
              <Check className="h-8 w-8 text-green-600" />
            </div>
            <h3 className="text-xl font-semibold">Import Complete</h3>
            <p className="text-muted-foreground text-sm max-w-xs">{importSummary}</p>
            {importNotes.length > 0 && (
              <ul className="text-muted-foreground text-sm max-w-lg list-disc pl-5 space-y-1.5 text-left">
                {importNotes.map((note, i) => <li key={i}>{note}</li>)}
              </ul>
            )}
            {awaiting.length > 0 && (
              <Button variant="outline" size="sm" className="rounded-full" onClick={handleRetryBooking} disabled={retryingBooking}>
                {retryingBooking ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Booking...</>
                ) : (
                  'Retry booking'
                )}
              </Button>
            )}
          </div>
        );
    }
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <Sheet open={open} onOpenChange={handleClose}>
      <SheetContent
        side="right"
        className="!inset-y-4 !right-4 !h-auto !p-0 !w-[90vw] !max-w-5xl rounded-2xl border shadow-2xl bg-background overflow-hidden flex gap-0"
      >
        {/* Left Sidebar */}
        <div className="w-[280px] bg-muted/30 border-r border-border/50 p-6 hidden md:flex flex-col relative overflow-hidden flex-shrink-0">
          <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-primary/5 to-transparent pointer-events-none" />
          <div className="absolute bottom-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl translate-x-1/2 translate-y-1/2 pointer-events-none" />

          <div className="mb-6 relative z-10">
            <h2 className="text-2xl font-semibold tracking-tight">Import Statement</h2>
            <p className="text-sm text-muted-foreground mt-1">Bulk import GT Bank transactions</p>
          </div>

          <div className="space-y-1 relative z-10 flex-1">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              const isActive = i === step;
              const isComplete = isStepComplete(i);

              return (
                <button
                  key={s.key}
                  onClick={() => { if (i <= step || isStepComplete(i - 1)) setStep(i); }}
                  disabled={i > step && !isStepComplete(i - 1)}
                  className={cn(
                    'w-full group flex items-center gap-3 p-3 rounded-xl transition-all duration-300 text-left',
                    isActive ? 'bg-background shadow-sm ring-1 ring-border' : 'hover:bg-background/50',
                    i > step && !isStepComplete(i - 1) && 'opacity-50 cursor-not-allowed',
                  )}
                >
                  <div className="relative">
                    <div className={cn(
                      'h-10 w-10 rounded-full flex items-center justify-center transition-colors duration-300',
                      isActive ? 'bg-primary text-primary-foreground' :
                        isComplete ? 'bg-green-100 text-green-600/70' : 'bg-muted text-muted-foreground',
                    )}>
                      <Icon className={cn('h-5 w-5', isComplete && !isActive && 'opacity-60')} />
                    </div>
                    {isComplete && !isActive && (
                      <div className="absolute -bottom-0.5 -right-0.5 h-4 w-4 rounded-full bg-green-500 flex items-center justify-center ring-2 ring-background">
                        <Check className="h-2.5 w-2.5 text-white" />
                      </div>
                    )}
                  </div>
                  <div>
                    <p className={cn('font-medium text-sm transition-colors', isActive ? 'text-foreground' : 'text-muted-foreground')}>{s.label}</p>
                    <p className="text-xs text-muted-foreground/70 hidden lg:block">{s.description}</p>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="mt-auto flex items-center relative z-10">
            <Button variant="ghost" size="sm" className="w-fit text-muted-foreground hover:text-foreground p-0 h-auto hover:bg-transparent" onClick={handleClose}>
              <X className="h-4 w-4 mr-2" />
              Close
            </Button>
          </div>
        </div>

        {/* Right Content */}
        <div className="flex-1 flex flex-col h-full relative min-w-0">
          {/* Mobile header */}
          <div className="md:hidden p-4 border-b flex items-center justify-between">
            <div>
              <h2 className="font-semibold">{STEPS[step].label}</h2>
              <p className="text-xs text-muted-foreground">Step {step + 1} of {STEPS.length}</p>
            </div>
            <Button variant="ghost" size="icon" onClick={handleClose}><X className="h-5 w-5" /></Button>
          </div>

          <div className="flex-1 flex flex-col overflow-hidden">
            <div className="flex flex-col h-full w-full">
              <div className="flex-none mb-4 px-6 md:px-8 pt-6 md:pt-8">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-2xl font-bold tracking-tight">{STEPS[step].label}</h3>
                    <p className="text-muted-foreground">{STEPS[step].description}</p>
                  </div>
                  {isStepComplete(step) && (
                    <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-100 text-green-700 text-xs font-medium">
                      <Check className="h-3.5 w-3.5" />
                      Complete
                    </div>
                  )}
                </div>
              </div>

              <div className="flex-1 min-h-0 overflow-auto px-6 md:px-8 pb-6">
                {renderStepContent()}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 border-t bg-background/80 backdrop-blur-md sticky bottom-0 z-20">
            <div className="flex items-center justify-between">
              {step === 0 && (
                <>
                  <Button variant="outline" onClick={handleClose} className="rounded-full">Cancel</Button>
                  <Button
                    onClick={handleProcess}
                    disabled={!bankFile || processing}
                    className="rounded-full shadow-lg hover:shadow-primary/25 transition-all"
                  >
                    {processing ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{processingStatus}</>
                    ) : (
                      <><FileSpreadsheet className="h-4 w-4 mr-2" />Process Statement</>
                    )}
                  </Button>
                </>
              )}

              {step === 1 && (
                <>
                  <Button
                    variant="outline"
                    onClick={() => { setStep(0); setTransactions([]); }}
                    className="rounded-full"
                  >
                    ← Back
                  </Button>
                  <Button
                    onClick={handleConfirm}
                    disabled={selected.size === 0 || confirming}
                    className="rounded-full shadow-lg hover:shadow-primary/25 transition-all"
                  >
                    {confirming ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Importing...</>
                    ) : (
                      <><Check className="h-4 w-4 mr-2" />Import {summaryStats.selectedCount} transaction{summaryStats.selectedCount !== 1 ? 's' : ''}</>
                    )}
                  </Button>
                </>
              )}

              {step === 2 && (
                <>
                  <div />
                  <Button onClick={handleClose} className="rounded-full shadow-lg hover:shadow-primary/25 transition-all">Done</Button>
                </>
              )}
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
